import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { authApi } from '../lib/api'

function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('Invalid or expired reset link. Please request a new one.')
    }
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setIsLoading(true)

    try {
      await authApi.resetPassword({ token: token!, newPassword: password })
      setSuccess(true)
      setTimeout(() => navigate('/login'), 3000)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', '--bg-primary': '#F7FAF7', '--forest-green': '#1B5E20', '--emerald': '#2E7D32', '--bg-tint': '#E8F5E9', '--slate': '#4A5568', '--slate-light': '#718096', '--border': '#E2E8F0', '--text-primary': '#1A202C', '--text-secondary': '#4A5568', '--text-muted': '#A0AEC0', '--success-bg': '#C6F6D5', '--success-text': '#276749', '--success-border': '#9AE6B4', '--error-bg': '#FED7D7', '--error-text': '#C53030', '--error-border': '#FC8181', '--shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'} as React.CSSProperties}>
      <header style={{background: '#fff', borderBottom: '1px solid var(--border)', padding: '1rem 2rem'}}>
        <Link to="/" style={{fontSize: '1.5rem', fontWeight: 700, color: 'var(--forest-green)', textDecoration: 'none'}}>
          I Got That U.S.
        </Link>
      </header>

      <main style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'}}>
        <div style={{width: '100%', maxWidth: '400px', background: '#fff', padding: '2rem', borderRadius: '12px', boxShadow: 'var(--shadow)'}}>
          <h1 style={{fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-primary)'}}>
            Reset Password
          </h1>
          <p style={{color: 'var(--text-secondary)', marginBottom: '1.5rem'}}>
            Enter your new password
          </p>

          {success ? (
            <div style={{padding: '1rem', background: 'var(--bg-tint)', borderRadius: '8px'}}>
              <p style={{color: 'var(--forest-green)', margin: 0}}>
                Password reset successful! Redirecting to login...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div style={{padding: '0.75rem', background: 'var(--error-bg)', borderRadius: '8px', marginBottom: '1rem', color: 'var(--error-text)', border: '1px solid var(--error-border)'}}>
                  {error}
                </div>
              )}

              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.5rem'}}>
                  New Password
                </label>
                <div style={{position: 'relative'}}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                    disabled={!token}
                    style={{width: '100%', padding: '0.75rem', paddingRight: '4rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--slate-light)', cursor: 'pointer'}}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.5rem'}}>
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={!token}
                  style={{width: '100%', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !token}
                style={{width: '100%', padding: '0.875rem', background: 'var(--forest-green)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 600, cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.7 : 1}}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          <p style={{textAlign: 'center', marginTop: '1.5rem', color: 'var(--text-secondary)'}}>
            Remember your password?{' '}
            <Link to="/login" style={{color: 'var(--forest-green)', fontWeight: 600, textDecoration: 'none'}}>
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  )
}

export default ResetPassword
